import React from 'react';
import PropTypes from 'prop-types';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { Label } from 'src/components/ui/label';
import { cn } from 'src/lib/utils';

// Reusable card-style radio group — pass `options` as
// [{ value, label, description?, disabled? }]. Used wherever a form needs a
// single-select choice with more context than a plain <Select> can show
// (e.g. picking an assignment flow).
const AppRadioGroup = ({
  options,
  value,
  onValueChange,
  name,
  disabled,
  orientation,
  className,
  itemClassName,
}) => (
  <RadioGroup
    value={value}
    onValueChange={onValueChange}
    name={name}
    disabled={disabled}
    className={cn(
      orientation === 'horizontal'
        ? 'flex flex-wrap gap-2.5'
        : 'flex flex-col gap-2',
      className
    )}
  >
    {options.map((option) => {
      const optionId = `${name || 'radio'}-${option.value}`;
      const isSelected = value === option.value;
      const isDisabled = disabled || option.disabled;
      return (
        <Label
          key={option.value}
          htmlFor={optionId}
          className={cn(
            'flex items-start gap-3 rounded-lg border px-3.5 py-2.5 cursor-pointer transition-colors font-normal',
            isSelected
              ? 'border-primary bg-brand-bg-subtle'
              : 'border-input hover:bg-muted/40',
            isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
            itemClassName
          )}
        >
          <RadioGroupItem
            value={option.value}
            id={optionId}
            disabled={isDisabled}
            className="mt-0.5 shrink-0"
          />
          <span className="flex flex-col gap-0.5">
            <span className="text-sm font-medium text-foreground-strong">
              {option.label}
            </span>
            {option.description && (
              <span className="text-xs text-muted-foreground">
                {option.description}
              </span>
            )}
          </span>
        </Label>
      );
    })}
  </RadioGroup>
);

AppRadioGroup.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.node.isRequired,
      description: PropTypes.node,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onValueChange: PropTypes.func.isRequired,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  orientation: PropTypes.oneOf(['vertical', 'horizontal']),
  className: PropTypes.string,
  itemClassName: PropTypes.string,
};

AppRadioGroup.defaultProps = {
  value: undefined,
  name: undefined,
  disabled: false,
  orientation: 'vertical',
  className: undefined,
  itemClassName: undefined,
};

export default AppRadioGroup;
